import React, { useMemo, useState } from "react";
import { Box, Button, Typography, alpha, useTheme } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { UnfoldLessRounded, UnfoldMoreRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import type {
  ClientKpi,
  ClientPortalDetailDay,
  ClientPortalSubcampaign,
} from "../../store/apis/clientPortalApi";
import {
  ChartModeSwitch,
  ClientCard,
  PillToggle,
  useChartMode,
} from "./ClientUiViewClient";
import {
  LegendStroke,
  progressSeriesElements,
} from "./ProgressSeriesViewClient";
import {
  ChartMode,
  formatCompact,
  formatMoney,
  formatNumber,
  formatPercent,
  formatShortDate,
  formatUnitMoney,
  forecastSeries,
  metricSeriesColor,
  seriesColor,
  toDailySeries,
} from "./clientFormatViewClient";

// Las dos gráficas de una campaña o subcampaña: cada una con su métrica, las
// dos con el mismo rango de fechas y el mismo modo (diario o acumulado). Con
// "Comparar" se juntan en una sola, cada métrica con su escala (izquierda y
// derecha).

export type ChartMetric =
  | "impressions"
  | "clicks"
  | "ctr"
  | "conversions"
  | "viewability"
  | "investment"
  | "cpm"
  | "cpc";

const METRICS: ChartMetric[] = [
  "impressions",
  "clicks",
  "ctr",
  "conversions",
  "viewability",
  "investment",
  "cpm",
  "cpc",
];

/** Un día de la campaña o subcampaña, con lo que se necesita para cada métrica. */
export interface MetricDay {
  date: string;
  /** Ya ha pasado (hasta ayer): tiene datos reales. */
  real: boolean;
  impressions: number;
  clicks: number;
  conversions: number;
  completeViews: number;
  engagements: number;
  measurableImpressions: number;
  viewableImpressions: number;
  investment: number;
  /** Lo previsto ese día del KPI del objetivo. null sin plan */
  plannedKpi: number | null;
  /** Lo previsto ese día de inversión. null sin presupuesto */
  plannedInvestment: number | null;
}

/**
 * Los días de una o varias subcampañas, de su primer a su último día. Lo de
 * cada día sale del detalle cuando ha llegado (trae conversiones y
 * visibilidad) y, mientras, del resumen; lo previsto, del resumen.
 */
export const buildMetricDays = (
  subs: ClientPortalSubcampaign[],
  kpi: ClientKpi,
  asOfDate: string,
  detail?: ClientPortalDetailDay[]
): MetricDay[] => {
  const byDate = new Map<string, MetricDay>();
  const day = (date: string) => {
    let row = byDate.get(date);
    if (!row) {
      row = {
        date,
        real: date <= asOfDate,
        impressions: 0,
        clicks: 0,
        conversions: 0,
        completeViews: 0,
        engagements: 0,
        measurableImpressions: 0,
        viewableImpressions: 0,
        investment: 0,
        plannedKpi: null,
        plannedInvestment: null,
      };
      byDate.set(date, row);
    }
    return row;
  };
  const add = (value: number | null, current: number | null) =>
    value === null ? current : (current ?? 0) + value;
  for (const sub of subs) {
    const daily = toDailySeries(sub.series);
    daily.forEach((point) => {
      const row = day(point.date);
      if (!detail) {
        row.impressions += point.impressions ?? 0;
        row.clicks += point.clicks ?? 0;
        row.investment += point.investment ?? 0;
        // El resumen no trae conversiones, vistas ni interacciones, pero sí lo
        // conseguido del objetivo: mientras llega el detalle, de ahí.
        if (sub.kpi === kpi && kpi !== "impressions" && kpi !== "clicks")
          row[kpi] += point.delivered ?? 0;
      }
      if (sub.kpi === kpi && sub.planned)
        row.plannedKpi = add(point.planned, row.plannedKpi);
      if (sub.budget)
        row.plannedInvestment = add(
          point.plannedInvestment,
          row.plannedInvestment
        );
    });
  }
  for (const d of detail ?? []) {
    const row = day(d.date);
    row.impressions = d.impressions;
    row.clicks = d.clicks;
    row.conversions = d.conversions;
    row.completeViews = d.completeViews;
    row.engagements = d.engagements;
    row.measurableImpressions = d.measurableImpressions;
    row.viewableImpressions = d.viewableImpressions;
    row.investment = d.investment;
  }
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
};

/** Lo sumado en un rango (solo días que ya han pasado) y lo previsto en él. */
export interface RangeTotals {
  impressions: number;
  clicks: number;
  conversions: number;
  completeViews: number;
  engagements: number;
  measurableImpressions: number;
  viewableImpressions: number;
  investment: number;
  /** KPI previsto en el rango. null sin plan */
  plannedKpi: number | null;
  /** Inversión prevista en el rango. null sin presupuesto */
  plannedInvestment: number | null;
}

export const sumDays = (
  days: MetricDay[],
  from: string,
  to: string
): RangeTotals => {
  const totals: RangeTotals = {
    impressions: 0,
    clicks: 0,
    conversions: 0,
    completeViews: 0,
    engagements: 0,
    measurableImpressions: 0,
    viewableImpressions: 0,
    investment: 0,
    plannedKpi: null,
    plannedInvestment: null,
  };
  for (const d of days) {
    if (d.date < from || d.date > to) continue;
    if (d.plannedKpi !== null)
      totals.plannedKpi = (totals.plannedKpi ?? 0) + d.plannedKpi;
    if (d.plannedInvestment !== null)
      totals.plannedInvestment =
        (totals.plannedInvestment ?? 0) + d.plannedInvestment;
    if (!d.real) continue;
    totals.impressions += d.impressions;
    totals.clicks += d.clicks;
    totals.conversions += d.conversions;
    totals.completeViews += d.completeViews;
    totals.engagements += d.engagements;
    totals.measurableImpressions += d.measurableImpressions;
    totals.viewableImpressions += d.viewableImpressions;
    totals.investment += d.investment;
  }
  return totals;
};

/**
 * El objetivo dentro de un rango: lo conseguido y lo previsto en esas fechas,
 * acumulados desde su primer día. Con toda la campaña es el objetivo entero.
 */
export const goalInRange = (
  days: MetricDay[],
  kpi: ClientKpi,
  from: string,
  to: string
) => {
  let delivered = 0;
  let planned = 0;
  let hasPlan = false;
  const series = days
    .filter((d) => d.date >= from && d.date <= to)
    .map((d) => {
      if (d.real) delivered += d[kpi];
      if (d.plannedKpi !== null) {
        planned += d.plannedKpi;
        hasPlan = true;
      }
      return {
        date: d.date,
        delivered: d.real ? delivered : null,
        planned: d.plannedKpi === null ? null : planned,
      };
    });
  return { delivered, planned: hasPlan ? planned : null, series };
};

const ADDITIVE = new Set<ChartMetric>([
  "impressions",
  "clicks",
  "conversions",
  "investment",
]);
const MONEY = new Set<ChartMetric>(["investment", "cpm", "cpc"]);
const PERCENT = new Set<ChartMetric>(["ctr", "viewability"]);

// Las que no tienen color propio (impresiones azul, clics naranja, dinero
// verde) llevan uno fijo de la paleta general, siempre el mismo.
const OWN_COLOR: Partial<Record<ChartMetric, number>> = {
  ctr: 6,
  conversions: 4,
  viewability: 3,
};

const metricColor = (metric: ChartMetric, mode: "light" | "dark") =>
  OWN_COLOR[metric] !== undefined
    ? seriesColor(OWN_COLOR[metric] as number, mode)
    : metricSeriesColor(metric, 0, mode);

/** El valor de una métrica con lo sumado hasta ahí (un día, o el acumulado). */
const valueOf = (
  metric: ChartMetric,
  t: {
    impressions: number;
    clicks: number;
    conversions: number;
    measurableImpressions: number;
    viewableImpressions: number;
    investment: number;
  }
): number | null => {
  switch (metric) {
    case "ctr":
      return t.impressions > 0 ? t.clicks / t.impressions : null;
    case "viewability":
      return t.measurableImpressions > 0
        ? t.viewableImpressions / t.measurableImpressions
        : null;
    case "cpm":
      return t.impressions > 0 ? (t.investment * 1000) / t.impressions : null;
    case "cpc":
      return t.clicks > 0 ? t.investment / t.clicks : null;
    default:
      return t[metric];
  }
};

interface Point {
  date: string;
  value: number | null;
  forecast: number | null;
  planned: number | null;
}

/**
 * La métrica de cada día del rango. En acumulado se suma desde el primer día
 * (lo que lleva conseguido, aunque el rango empiece más tarde). Predicción y
 * previsto, solo en las métricas que se suman.
 */
const metricPoints = (
  days: MetricDay[],
  metric: ChartMetric,
  mode: ChartMode,
  kpi: ClientKpi,
  from: string,
  to: string
): Point[] => {
  const totals = {
    impressions: 0,
    clicks: 0,
    conversions: 0,
    measurableImpressions: 0,
    viewableImpressions: 0,
    investment: 0,
  };
  let planTotal = 0;
  const planOf = (d: MetricDay) =>
    metric === "investment"
      ? d.plannedInvestment
      : metric === kpi
        ? d.plannedKpi
        : null;
  const all = days.map((d) => {
    if (d.real) {
      totals.impressions += d.impressions;
      totals.clicks += d.clicks;
      totals.conversions += d.conversions;
      totals.measurableImpressions += d.measurableImpressions;
      totals.viewableImpressions += d.viewableImpressions;
      totals.investment += d.investment;
    }
    const plan = planOf(d);
    if (plan !== null) planTotal += plan;
    const cumulative = d.real ? valueOf(metric, totals) : null;
    return {
      date: d.date,
      value: d.real
        ? mode === "daily"
          ? valueOf(metric, d)
          : cumulative
        : null,
      cumulative,
      planned: plan === null ? null : mode === "daily" ? plan : planTotal,
    };
  });
  // La regla de siempre, sobre el acumulado: cada día que queda suma lo de ayer.
  const forecast = ADDITIVE.has(metric)
    ? forecastSeries(
        all.map((p) => p.cumulative),
        mode
      )
    : null;
  return all
    .map((p, i) => ({
      date: p.date,
      value: p.value,
      planned: p.planned,
      forecast: forecast?.[i] ?? null,
    }))
    .filter((p) => p.date >= from && p.date <= to);
};

const metricLabelKey = (metric: ChartMetric) =>
  metric === "viewability"
    ? "clientView.detail.viewability"
    : metric === "cpm" || metric === "cpc"
      ? `clientView.money.${metric}`
      : `clientView.stats.${metric}`;

/** Cómo se escribe una cifra de la métrica: en el eje (corta) o exacta. */
const useMetricFormat = (currency: string) => {
  const { i18n } = useTranslation();
  const language = i18n.language;
  return (metric: ChartMetric, value: number, exact = false) =>
    PERCENT.has(metric)
      ? formatPercent(value, language, exact ? 2 : 1)
      : metric === "investment"
        ? exact
          ? formatUnitMoney(value, currency, language)
          : formatMoney(value, currency, language)
        : MONEY.has(metric)
          ? formatUnitMoney(value, currency, language)
          : exact
            ? formatNumber(value, language)
            : formatCompact(value, language);
};

const CHART_HEIGHT = 300;

// Las flechas del botón "Comparar" se aprietan y se sueltan: juntar.
const squeeze = keyframes`
  0%, 100% { transform: scaleY(1); }
  50% { transform: scaleY(0.6); }
`;

/** Una gráfica: una métrica o, comparando, dos con su escala cada una. */
const MetricChart: React.FC<{
  days: MetricDay[];
  metrics: [ChartMetric] | [ChartMetric, ChartMetric];
  mode: ChartMode;
  kpi: ClientKpi;
  from: string;
  to: string;
  currency: string;
}> = ({ days, metrics, mode, kpi, from, to, currency }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const language = i18n.language;
  const format = useMetricFormat(currency);
  const paletteMode = theme.palette.mode;
  const muted = theme.palette.neutral[600];
  const compare = metrics.length === 2;

  const rows = useMemo(() => {
    const series = metrics.map((m) =>
      metricPoints(days, m, mode, kpi, from, to)
    );
    return series[0].map((point, i) => ({
      date: point.date,
      d0: point.value,
      f0: compare ? null : point.forecast,
      p0: compare ? null : point.planned,
      d1: series[1]?.[i]?.value ?? null,
    }));
  }, [days, metrics, mode, kpi, from, to, compare]);

  const colors = metrics.map((m, i) => {
    const own = metricColor(m, paletteMode);
    // Dos métricas de la misma familia (CPM y CPC, verdes): la segunda cambia.
    return i === 1 && own === metricColor(metrics[0], paletteMode)
      ? seriesColor(6, paletteMode)
      : own;
  });
  const labels = metrics.map((m) => t(metricLabelKey(m)));
  const hasData = rows.some((r) => r.d0 !== null || r.d1 !== null);

  if (!hasData)
    return (
      <Box
        sx={{
          height: CHART_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ color: "text.secondary", fontSize: 15 }}>
          {t("clientView.charts.noData")}
        </Typography>
      </Box>
    );

  const renderTooltip = ({
    active,
    payload,
    label,
  }: TooltipProps<number, string>) => {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload as (typeof rows)[number];
    const line = (
      key: string,
      color: string,
      text: string,
      value: number | null,
      metric: ChartMetric
    ) =>
      value === null ? null : (
        <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }}
          />
          <Typography sx={{ fontSize: 12 }}>
            {text}{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {format(metric, value, true)}
            </Box>
          </Typography>
        </Box>
      );
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "14px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          px: 2,
          py: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
          {formatShortDate(String(label), language)}
        </Typography>
        {line("d0", colors[0], labels[0], row.d0, metrics[0])}
        {row.d0 === null &&
          line(
            "f0",
            theme.palette.neutral[400],
            `${labels[0]} · ${t("clientView.charts.forecast")}`,
            row.f0,
            metrics[0]
          )}
        {line(
          "p0",
          colors[0],
          `${labels[0]} · ${t("clientView.charts.planned")}`,
          row.p0,
          metrics[0]
        )}
        {compare &&
          line("d1", colors[1], labels[1], row.d1, metrics[1] as ChartMetric)}
      </Box>
    );
  };

  const daily = mode === "daily";
  const axis = (id: "left" | "right", metric: ChartMetric, color?: string) => (
    <YAxis
      yAxisId={id}
      orientation={id}
      tickFormatter={(v: number) => format(metric, v)}
      tick={{ fill: color ?? muted, fontSize: 12 }}
      axisLine={false}
      tickLine={false}
      width={60}
    />
  );

  return (
    <Box>
      {/* Qué mide cada escala: a la izquierda la primera, a la derecha la segunda. */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1,
          fontSize: 12,
        }}
      >
        <Typography sx={{ fontSize: 12, color: compare ? colors[0] : muted }}>
          {labels[0]}
        </Typography>
        {compare && (
          <Typography sx={{ fontSize: 12, color: colors[1] }}>
            {labels[1]}
          </Typography>
        )}
      </Box>
      <Box sx={{ width: "100%", height: CHART_HEIGHT }}>
        <ResponsiveContainer>
          <ComposedChart
            data={rows}
            margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          >
            <CartesianGrid vertical={false} stroke={theme.palette.divider} />
            <XAxis
              dataKey="date"
              tickFormatter={(d: string) => formatShortDate(d, language)}
              tick={{ fill: muted, fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              minTickGap={40}
              tickMargin={10}
            />
            {axis("left", metrics[0], compare ? colors[0] : undefined)}
            {compare && axis("right", metrics[1] as ChartMetric, colors[1])}
            <Tooltip
              content={renderTooltip}
              cursor={
                daily
                  ? { fill: theme.palette.neutral[100] }
                  : { stroke: theme.palette.neutral[400], strokeWidth: 1 }
              }
            />
            {compare
              ? [
                  daily ? (
                    <Bar
                      key="d0"
                      yAxisId="left"
                      dataKey="d0"
                      fill={colors[0]}
                      maxBarSize={22}
                      radius={[3, 3, 0, 0]}
                      isAnimationActive={false}
                    />
                  ) : (
                    <Line
                      key="d0"
                      yAxisId="left"
                      dataKey="d0"
                      stroke={colors[0]}
                      strokeWidth={2.5}
                      dot={false}
                      isAnimationActive={false}
                    />
                  ),
                  <Line
                    key="d1"
                    yAxisId="right"
                    dataKey="d1"
                    stroke={colors[1]}
                    strokeWidth={2.5}
                    dot={daily ? { r: 2.5, fill: colors[1] } : false}
                    isAnimationActive={false}
                  />,
                ]
              : progressSeriesElements({
                  series: [
                    {
                      key: "0",
                      name: labels[0],
                      color: colors[0],
                      hasPlan: rows.some((r) => r.p0 !== null),
                      hasForecast: rows.some((r) => r.f0 !== null),
                    },
                  ],
                  daily,
                  stacked: false,
                  background: theme.palette.background.paper,
                  forecastColor: theme.palette.neutral[400],
                }).map((el) =>
                  React.cloneElement(el as React.ReactElement, {
                    yAxisId: "left",
                  })
                )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      {!compare && (
        <Box sx={{ mt: 2, display: "flex", gap: 2.5, flexWrap: "wrap" }}>
          <LegendStroke
            label={t("clientView.charts.delivered")}
            color={muted}
            bar={daily ? "solid" : undefined}
          />
          {rows.some((r) => r.f0 !== null) && (
            <LegendStroke
              label={t("clientView.charts.forecast")}
              color={muted}
              dash="1 4"
              bar={daily ? "faded" : undefined}
            />
          )}
          {rows.some((r) => r.p0 !== null) && (
            <LegendStroke
              label={t("clientView.charts.planned")}
              color={muted}
              dash="5 4"
            />
          )}
        </Box>
      )}
    </Box>
  );
};

/** Pastilla "Objetivo" junto a la métrica que es el objetivo de la campaña. */
const GoalBadge: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        ml: 0.75,
        px: 0.75,
        py: 0.125,
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        lineHeight: 1.4,
        color: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.14),
        verticalAlign: "middle",
      }}
    >
      {t("clientView.goal.badge")}
    </Box>
  );
};

/** El nombre de una métrica y, si es la del objetivo, su pastilla. */
const MetricName: React.FC<{ metric: ChartMetric; kpi: ClientKpi }> = ({
  metric,
  kpi,
}) => {
  const { t } = useTranslation();
  return (
    <>
      {t(metricLabelKey(metric))}
      {metric === kpi && <GoalBadge />}
    </>
  );
};

/** Elegir métrica: solo las que tienen datos; la del objetivo, marcada. */
const MetricPicker: React.FC<{
  value: ChartMetric;
  onChange: (metric: ChartMetric) => void;
  options: ChartMetric[];
  label: string;
  kpi: ClientKpi;
}> = ({ value, onChange, options, label, kpi }) => (
  <PillToggle
    value={value}
    onChange={onChange}
    label={label}
    options={options.map((m) => ({
      value: m,
      label: <MetricName metric={m} kpi={kpi} />,
    }))}
  />
);

/**
 * El modo arriba; dos gráficas, cada una con su métrica (las fechas son las
 * de la página); y, en medio, el botón para juntarlas o separarlas.
 */
const MetricChartsViewClient: React.FC<{
  days: MetricDay[];
  kpi: ClientKpi;
  currency: string;
  /** Fechas elegidas arriba de la página (ya resueltas). */
  from: string;
  to: string;
}> = ({ days, kpi, currency, from, to }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [mode, setMode] = useChartMode();
  const [compare, setCompare] = useState(false);
  // Al pasar por "Comparar", las dos gráficas se acercan un poco: anuncia que
  // se van a juntar.
  const [hinting, setHinting] = useState(false);
  const pull = (direction: 1 | -1) => ({
    transition: "transform 0.3s cubic-bezier(0.2, 0, 0, 1)",
    transform: hinting && !compare ? `translateY(${direction * 10}px)` : "none",
    "@media (prefers-reduced-motion: reduce)": { transform: "none" },
  });

  const options = useMemo(() => {
    const has = (pick: (d: MetricDay) => number) =>
      days.some((d) => pick(d) > 0);
    return METRICS.filter(
      (m) =>
        (m !== "conversions" || has((d) => d.conversions)) &&
        (m !== "viewability" || has((d) => d.measurableImpressions)) &&
        (!MONEY.has(m) || has((d) => d.investment))
    );
  }, [days]);
  const kpiMetric = (METRICS as string[]).includes(kpi)
    ? (kpi as ChartMetric)
    : "impressions";
  const [first, setFirst] = useState<ChartMetric>(kpiMetric);
  const [second, setSecond] = useState<ChartMetric>("investment");
  // Si la métrica elegida no tiene datos aquí, la primera que sí.
  const metricA = options.includes(first) ? first : options[0];
  const metricB = options.includes(second)
    ? second
    : (options.find((m) => m !== metricA) ?? options[0]);

  const chart = (metrics: [ChartMetric] | [ChartMetric, ChartMetric]) => (
    <MetricChart
      days={days}
      metrics={metrics}
      mode={mode}
      kpi={kpi}
      from={from}
      to={to}
      currency={currency}
    />
  );

  const toggle = (
    <Box
      sx={{ display: "flex", justifyContent: "center", my: compare ? 0 : -1 }}
    >
      <Button
        onClick={() => {
          setHinting(false);
          setCompare((c) => !c);
        }}
        onMouseEnter={() => setHinting(true)}
        onMouseLeave={() => setHinting(false)}
        onFocus={() => setHinting(true)}
        onBlur={() => setHinting(false)}
        startIcon={compare ? <UnfoldMoreRounded /> : <UnfoldLessRounded />}
        sx={{
          position: "relative",
          zIndex: 1,
          textTransform: "none",
          fontWeight: 700,
          borderRadius: 999,
          px: 2,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          color: "text.primary",
          transition:
            "transform 0.2s ease, background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
          "&:hover, &:focus-visible": {
            transform: "scale(1.08)",
            backgroundColor: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.18)}, 0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
            "& .MuiButton-startIcon": {
              animation: `${squeeze} 0.7s ease-in-out infinite`,
            },
          },
          "@media (prefers-reduced-motion: reduce)": {
            "&:hover, &:focus-visible": {
              transform: "none",
              "& .MuiButton-startIcon": { animation: "none" },
            },
          },
        }}
      >
        {t(
          compare ? "clientView.compare.separate" : "clientView.compare.compare"
        )}
      </Button>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <ChartModeSwitch value={mode} onChange={setMode} />
      </Box>

      {compare ? (
        <>
          <ClientCard
            title={t("clientView.compare.title")}
            action={
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <MetricPicker
                  kpi={kpi}
                  value={metricA}
                  onChange={setFirst}
                  options={options}
                  label={t("clientView.compare.left")}
                />
                <MetricPicker
                  kpi={kpi}
                  value={metricB}
                  onChange={setSecond}
                  options={options}
                  label={t("clientView.compare.right")}
                />
              </Box>
            }
          >
            {chart([metricA, metricB])}
          </ClientCard>
          {toggle}
        </>
      ) : (
        <>
          <ClientCard
            title={<MetricName metric={metricA} kpi={kpi} />}
            action={
              <MetricPicker
                kpi={kpi}
                value={metricA}
                onChange={setFirst}
                options={options}
                label={t("clientView.charts.metricLabel")}
              />
            }
            sx={pull(1)}
          >
            {chart([metricA])}
          </ClientCard>
          {toggle}
          <ClientCard
            sx={pull(-1)}
            title={<MetricName metric={metricB} kpi={kpi} />}
            action={
              <MetricPicker
                kpi={kpi}
                value={metricB}
                onChange={setSecond}
                options={options}
                label={t("clientView.charts.metricLabel")}
              />
            }
          >
            {chart([metricB])}
          </ClientCard>
        </>
      )}
    </Box>
  );
};

export default MetricChartsViewClient;

/**
 * Con toda la campaña, los totales del resumen: son exactos desde el primer
 * momento (aunque el detalle, que trae conversiones, vistas e interacciones
 * día a día, aún no haya llegado) y el presupuesto es el de cada subcampaña.
 */
export const lifetimeTotals = (
  subs: ClientPortalSubcampaign[],
  totals: RangeTotals
): RangeTotals => {
  const sum = (
    key:
      | "impressions"
      | "clicks"
      | "completeViews"
      | "conversions"
      | "engagements"
      | "investment"
  ) => subs.reduce((total, s) => total + s[key], 0);
  return {
    ...totals,
    impressions: sum("impressions"),
    clicks: sum("clicks"),
    completeViews: sum("completeViews"),
    conversions: sum("conversions"),
    engagements: sum("engagements"),
    investment: sum("investment"),
    plannedInvestment: subs.every((s) => s.budget)
      ? subs.reduce((total, s) => total + (s.budget ?? 0), 0)
      : totals.plannedInvestment,
  };
};
